import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from 'src/utils/storage';
import { getUserIdFromToken, isTokenExpired } from 'src/utils/jwt';

type User = {
  id: string;
  email: string;
};

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  login: (token: string, email: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(storage.get('token'));
  const [user, setUser] = useState<User | null>(null);

  const isAuthenticated = Boolean(token) && !isTokenExpired(); // FE-1: exp doğrulanır

  const logout = useCallback(() => {
    storage.remove('token');
    setToken(null);
    setUser(null);
    navigate('/sign-in');
  }, [navigate]);

  // Token süresi dolduğunda otomatik logout
  useEffect(() => {
    const unsubscribe = storage.onTokenExpired(() => {
      logout();
    });
    return () => unsubscribe();
  }, [logout]);

  // Active checking loop to detect token expiration while the tab is idle
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      if (isTokenExpired() || !storage.get('token')) {
        console.log('AuthContext: Active expiration check failed, performing auto-logout');
        logout();
      }
    }, 15000); // Check every 15 seconds
    return () => clearInterval(interval);
  }, [token, logout]);

  // Component mount olduğunda token varsa user'ı yükle
  useEffect(() => {
    if (token) {
      if (isTokenExpired()) {
        // FE-1: süresi dolmuş token mount/yenilemede temizlenir → /sign-in
        logout();
        return;
      }
      const userId = getUserIdFromToken();
      if (userId) {
        // email bilgisini storage/decode edebilirsiniz ya da updateMyAccount çağırın
        setUser({ id: userId, email: '' /* eksikse fetch edin */ });
      } else {
        // Token geçersizse çıkış yap
        logout();
      }
    }
  }, [token, logout]);

  /** 
   * Yeni: token bazlı login 
   */
  const login = useCallback((newToken: string, email: string) => {
    // 1) Token'ı storage'a yaz
    storage.set('token', newToken);
    // 2) Context state'lerini güncelle
    setToken(newToken);
    const userId = getUserIdFromToken();
    setUser({ id: userId ?? '', email });
    // 3) Dashboard'a yönlendir
    navigate('/dashboard');
  }, [navigate]);

  const value = useMemo(
    () => ({ isAuthenticated, user, login, logout }),
    [isAuthenticated, user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
